import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { BookOpen, ShoppingBag, Link2, Menu, LogOut, Feather, CalendarDays, Heart, Video } from "lucide-react";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { useAuth } from "../lib/auth";
import AffiliateSidebar from "./AffiliateSidebar";

const NAV = [
  { to: "/bible", label: "Bible", icon: BookOpen, testid: "nav-bible" },
  { to: "/plans", label: "Reading Plans", icon: CalendarDays, testid: "nav-plans" },
  { to: "/articles", label: "Articles", icon: Feather, testid: "nav-articles" },
  { to: "/videos", label: "Videos", icon: Video, testid: "nav-videos" },
  { to: "/bookmarks", label: "Bookmarks", icon: Heart, testid: "nav-bookmarks" },
  { to: "/tracts", label: "Gospel Tracts", icon: ShoppingBag, testid: "nav-tracts" },
  { to: "/resources", label: "Resources", icon: Link2, testid: "nav-resources" },
];

export default function Layout({ children, hideSidebar = false }) {
  const { admin, logout } = useAuth();
  const loc = useLocation();
  const isAdminArea = loc.pathname.startsWith("/admin");

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-40 border-b border-stone-200/80 bg-stone-50/85 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground font-serif text-lg">✝</span>
            <span className="font-serif text-xl text-stone-900">Know Bible</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "text-primary bg-stone-100" : "text-stone-700 hover:text-primary hover:bg-stone-100"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {admin ? (
              <>
                <Link to="/admin" data-testid="nav-admin-dashboard">
                  <Button variant="outline" size="sm">Admin</Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={logout} data-testid="nav-logout">
                  <LogOut className="h-4 w-4 mr-1" strokeWidth={1.5} /> Logout
                </Button>
              </>
            ) : (
              <Link to="/admin/login" data-testid="nav-admin-login">
                <Button variant="ghost" size="sm">Admin</Button>
              </Link>
            )}
          </div>

          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" strokeWidth={1.5} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-6 flex flex-col gap-1">
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    data-testid={`m-${n.testid}`}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-md text-base ${isActive ? "text-primary bg-stone-100 font-medium" : "text-stone-800 hover:bg-stone-100"}`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
                <div className="mt-2 border-t border-stone-200 pt-2">
                  {admin ? (
                    <>
                      <Link to="/admin" className="block px-4 py-3 text-stone-800 hover:bg-stone-100 rounded-md">Admin Dashboard</Link>
                      <button onClick={logout} className="block w-full text-left px-4 py-3 text-stone-800 hover:bg-stone-100 rounded-md">Logout</button>
                    </>
                  ) : (
                    <Link to="/admin/login" className="block px-4 py-3 text-stone-800 hover:bg-stone-100 rounded-md">Admin Login</Link>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="flex-1">
        {isAdminArea || hideSidebar ? (
          <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-10">{children}</div>
        ) : (
          <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-10 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
            <div className="min-w-0">{children}</div>
            <aside className="hidden lg:block">
              <AffiliateSidebar />
            </aside>
          </div>
        )}
      </main>

      <footer className="border-t border-stone-200 bg-stone-50">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12 py-10 grid gap-6 sm:grid-cols-3 text-sm text-stone-600">
          <div>
            <div className="font-serif text-lg text-stone-900 mb-2">Know Bible</div>
            <p className="leading-relaxed">Bringing scripture, gospel tracts, and trusted Christian resources into one quiet place.</p>
          </div>
          <div>
            <div className="font-medium text-stone-900 mb-2">Explore</div>
            <ul className="space-y-1">
              <li><Link to="/bible" className="hover:text-primary">Read the Bible</Link></li>
              <li><Link to="/tracts" className="hover:text-primary">Order Tracts</Link></li>
              <li><Link to="/resources" className="hover:text-primary">Christian Resources</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-medium text-stone-900 mb-2">Note</div>
            <p className="leading-relaxed">"Thy word is a lamp unto my feet, and a light unto my path." — Psalm 119:105</p>
          </div>
        </div>
        <div className="border-t border-stone-200 py-4 text-center text-xs text-stone-500">
          © {new Date().getFullYear()} Know Bible. All Scripture served with reverence.
        </div>
      </footer>
    </div>
  );
}
